import { useMutation, useQuery } from '@tanstack/react-query';

import {
  fetchMonthlyStatementRequestForm,
  fetchMonthlyStatementRequestFormInit,
  type MonthlyStatementRequestFormInitParams,
} from '@/services/main/rezept';
import { useClinicContext } from './useClinicContext';
import { useClinicPortalServiceType } from './clinicPortalServiceType';

/**
 * 令和8年法改正 No 3 — 月別明細書発行依頼書（別紙様式5）の雛型印刷.
 *
 * Opens the slip a patient signs to request the itemised statement monthly. The only
 * input is the optional 拠点 printed in the 宛名 (HIT-13991); no validation, and nothing
 * is marked 印刷済.
 *
 * Fetched as a blob rather than opened as a plain URL because the endpoint is behind
 * bearer auth — a bare window.open would arrive unauthenticated. The object URL is
 * revoked once the new tab has had a chance to load it.
 */
const OBJECT_URL_TTL_MS = 60_000;

export const useMonthlyStatementRequestForm = () => {
  const { tenant, clinicNumber } = useClinicContext();

  return useMutation({
    mutationFn: (clinicLocationId?: number | null) =>
      fetchMonthlyStatementRequestForm(tenant ?? '', clinicNumber ?? '', clinicLocationId),
    onSuccess: blob => {
      const url = URL.createObjectURL(blob);
      window.open(url, '_blank', 'noopener');
      window.setTimeout(() => URL.revokeObjectURL(url), OBJECT_URL_TTL_MS);
    },
  });
};

/** 「月別希望」 popup options, fetched each time the popup opens. */
export const useMonthlyStatementRequestFormInit = (
  params: MonthlyStatementRequestFormInitParams,
  enabled: boolean
) => {
  const { tenant, clinicNumber } = useClinicContext();
  const serviceType = useClinicPortalServiceType();

  return useQuery({
    queryKey: ['monthly-statement-request-form', 'init', tenant, clinicNumber, serviceType, params],
    queryFn: () => fetchMonthlyStatementRequestFormInit(tenant ?? '', clinicNumber ?? '', params),
    enabled: enabled && !!tenant && !!clinicNumber,
    staleTime: 0,
  });
};
